import React, { useState, useEffect } from 'react';
import { ArrowLeft, Award, Globe, Briefcase, Settings, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

import { apiUrl, openBookDemo } from '../utils/config';
import logger from '../utils/logger';

const Coaches = ({ onBackToHome, onAdminClick }) => {
  const { t, i18n } = useTranslation();
  const [coaches, setCoaches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCoaches();
  }, [i18n.language]);

  const fetchCoaches = async () => {
    try {
      const response = await fetch(apiUrl(`coaches?lang=${i18n.language}`));
      if (response.ok) {
        const data = await response.json();
        setCoaches(data);
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={onBackToHome}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{i18n.language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}</span>
          </button>
          {onAdminClick && (
            <button
              onClick={onAdminClick}
              className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors text-sm"
              data-testid="coaches-admin-btn"
            >
              <Settings className="w-4 h-4" />
              <span>{i18n.language === 'tr' ? 'Yönetici' : 'Admin'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="coaches-title">
            {t('pages.coaches.title')}
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            {t('pages.coaches.subtitle')}
          </p>
        </div>
      </section>

      {/* Coaches Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : coaches.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl max-w-2xl mx-auto shadow-md">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-4">{t('pages.coaches.noCoaches')}</p>
              <p className="text-gray-500">
                {i18n.language === 'tr' ? 'Dünya standartlarında koçlarımızla tanışmak için yakında tekrar kontrol edin.' : 'Check back soon to meet our world-class coaches.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coaches.map((coach) => (
                <div
                  key={coach.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  data-testid={`coach-card-${coach.id}`}
                >
                  {/* Coach Image */}
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center overflow-hidden">
                    {coach.image ? (
                      <img
                        src={coach.image}
                        alt={coach.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-blue-600 flex items-center justify-center text-white text-4xl font-bold">
                        {coach.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                  </div>
                  
                  {/* Coach Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{coach.name}</h3>
                    <p className="text-blue-600 font-medium mb-4">{coach.title}</p>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{coach.bio}</p>
                    
                    {/* Specialties */}
                    {coach.specialties && coach.specialties.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                          <Briefcase className="w-4 h-4" />
                          <span>{t('pages.coaches.specialties')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {coach.specialties.slice(0, 3).map((specialty, index) => (
                            <span
                              key={specialty}
                              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                            >
                              {specialty}
                            </span>
                          ))}
                          {coach.specialties.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{coach.specialties.length - 3} {i18n.language === 'tr' ? 'daha' : 'more'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Certifications */}
                    {coach.certifications && coach.certifications.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                          <Award className="w-4 h-4" />
                          <span>{t('pages.coaches.certifications')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {coach.certifications.map((cert, index) => (
                            <span
                              key={cert}
                              className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                            >
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Languages */}
                    {coach.languages && coach.languages.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Globe className="w-4 h-4" />
                        <span>{coach.languages.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {i18n.language === 'tr' ? 'Koçlarımızla Çalışmaya Hazır mısınız?' : 'Ready to Work With Our Coaches?'}
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'tr' ? 'Liderlik gelişim yolculuğunuz için mükemmel koçla eşleştirin.' : 'Get matched with the perfect coach for your leadership development journey.'}
          </p>
          <Button 
            className="bg-white text-blue-900 hover:bg-gray-100"
            onClick={openBookDemo}
            data-testid="contact-cta-btn"
          >
            {t('nav.bookDemo')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Coaches;
